/**
 * ErrorDetailsTable renders the editable error table for both property and
 * order bulk import flows. Delegates row generation to PropertyErrorRow /
 * OrderErrorRow and state management to useErrorTableData.
 */

import { CircleCheck, Loader2 } from 'lucide-react';

import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import type { Filters } from '@/types/table';

import { ErrorDetailsCard } from './components/ErrorDetailsCard';
import { PROPERTY_ERROR_COLUMNS, ERROR_TABLE_FILTERS } from './metadata/propertyErrorColumns';
import { ORDER_ERROR_COLUMNS } from './metadata/orderErrorColumns';
import { useErrorTableData } from './hooks/useErrorTableData';
import type { ErrorCell } from '../fix-errors/utils/fixErrorFieldMaps';

interface ErrorTableRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

interface ErrorDetailsTableProps {
  setResult: (data: ErrorTableRow[] | null) => void;
  ErrorTableData: ErrorTableRow[];
  setDisabled: (disabled: boolean) => void;
  isProperty: boolean;
  setFixErrorTableData: (data: string) => void;
  fixErrorTableData: string | null;
  setFileChangeAfterFix: (v: boolean) => void;
  fileChangeAfterFix: boolean;
  fixErrorTableDataForOrder: ErrorTableRow[] | undefined;
  setIsClearData: (v: boolean) => void;
  isClearData: boolean;
}

const ErrorDetailsTable = ({
  setResult,
  ErrorTableData,
  setDisabled,
  isProperty,
  setFixErrorTableData,
  fixErrorTableData,
  setFileChangeAfterFix,
  fileChangeAfterFix,
  fixErrorTableDataForOrder,
  setIsClearData,
  isClearData,
}: ErrorDetailsTableProps) => {
  const filters: Filters[] = ERROR_TABLE_FILTERS;

  const { tableData, isLoading } = useErrorTableData({
    ErrorTableData,
    isProperty,
    fixErrorTableData,
    fixErrorTableDataForOrder,
    fileChangeAfterFix,
    isClearData,
    setResult,
    setDisabled,
    setFixErrorTableData,
    setFileChangeAfterFix,
    setIsClearData,
  });

  const columns = isProperty ? PROPERTY_ERROR_COLUMNS : ORDER_ERROR_COLUMNS;
  const hasRows = tableData.length > 0;
  const showLoader = isLoading && !isProperty;
  const showEmptyState = !hasRows && !isLoading && !isProperty;

  return (
    // max-lg:-mx-4 cancels DataTable's built-in 16px card-list gutter (its
    // <ul> p-4, sized for unpadded hosts like the orders list) against this
    // page's padded section, so error cards align with the page's own gutter
    // instead of sitting doubly inset.
    <section className="flex min-h-0 flex-1 flex-col max-lg:-mx-4" aria-label="error-details-table">
      {showLoader ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
        </div>
      ) : showEmptyState ? (
        <EmptyState
          icon={CircleCheck}
          title="No Errors to Fix"
          description="Every row passed validation."
        />
      ) : (
        <DataTable
          columns={columns}
          tableData={tableData}
          filters={filters}
          isLoading={false}
          showCheckboxes={false}
          hideActionBar={true}
          renderMobileCard={(row) => <ErrorDetailsCard columns={columns} row={row} />}
        />
      )}
    </section>
  );
};

export default ErrorDetailsTable;
